import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, RotateCcw, Send, Sparkles, User as UserIcon } from "lucide-react";
import { Badge, Card, PrimaryButton } from "@/components/Card";
import { useDemoStore, type ChatMessage, type Role } from "@/store/useDemoStore";
import { buildIaReply, iaConfigByRole } from "@/data/iaResponses";

interface IaChatProps {
  role: Role;
}

const EMPTY_MESSAGES: ChatMessage[] = [];

export function IaChat({ role }: IaChatProps) {
  const cfg = iaConfigByRole[role];
  const messages = useDemoStore((s) => s.iaConversationsByRole[role] ?? EMPTY_MESSAGES);
  const appendIaMessage = useDemoStore((s) => s.appendIaMessage);
  const clearIaConversation = useDemoStore((s) => s.clearIaConversation);

  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const greeting = useMemo(() => cfg.greeting(useDemoStore.getState()), [cfg]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, typing]);

  function send(text: string) {
    const value = text.trim();
    if (!value || typing) return;
    appendIaMessage(role, { autor: "user", texto: value });
    setDraft("");
    setTyping(true);
    const delay = 600 + Math.floor(Math.random() * 300);
    timerRef.current = setTimeout(() => {
      const reply = buildIaReply(role, value, useDemoStore.getState());
      appendIaMessage(role, { autor: "ia", texto: reply });
      setTyping(false);
    }, delay);
  }

  function handleClear() {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTyping(false);
    clearIaConversation(role);
  }

  return (
    <Card padding={0} testId={`ia-chat-${role}`}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--cc-gray-200)",
          background: "linear-gradient(135deg, var(--cc-navy) 0%, #243259 100%)",
          color: "white",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: "var(--cc-teal)", color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Sparkles size={20} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }} data-testid="ia-agent-name">{cfg.agentName}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Rol: {role}</div>
            </div>
          </div>
          <Badge tone="amber">Demo · respuestas simuladas</Badge>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.85)", lineHeight: 1.55 }}>
          {cfg.description}
        </div>
      </div>

      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label={`Conversación con ${cfg.agentName}`}
        style={{ padding: "18px 20px", minHeight: 320, maxHeight: 460, overflowY: "auto", background: "var(--cc-gray-50, #fafbfc)" }}
        data-testid="ia-message-list"
      >
        <Bubble autor="ia" texto={greeting} agentName={cfg.agentName} />
        {messages.map((m) => (
          <Bubble key={m.id} autor={m.autor} texto={m.texto} agentName={cfg.agentName} ts={m.ts} />
        ))}
        {typing && <TypingBubble />}
      </div>

      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)", background: "white" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }} data-testid="ia-quick-chips">
          {cfg.quickChips.map((chip) => (
            <button
              key={chip}
              onClick={() => send(chip)}
              disabled={typing}
              data-testid={`ia-chip-${chip.slice(0, 20)}`}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid var(--cc-teal)",
                background: "var(--cc-teal-bg)",
                color: "var(--cc-teal)",
                fontSize: 12,
                fontWeight: 600,
                cursor: typing ? "not-allowed" : "pointer",
                opacity: typing ? 0.55 : 1,
              }}
            >
              {chip}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(draft); }}
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <label htmlFor={`ia-input-${role}`} style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            Escribe tu pregunta para {cfg.agentName}
          </label>
          <input
            id={`ia-input-${role}`}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Escribe tu pregunta..."
            data-testid="ia-input"
            disabled={typing}
            style={{
              flex: 1, padding: "10px 12px",
              border: "1px solid var(--cc-gray-200)",
              borderRadius: 8, fontSize: 13,
              background: "white", color: "var(--cc-gray-900)",
            }}
          />
          <PrimaryButton type="submit" testId="ia-send" disabled={typing || !draft.trim()}>
            <Send size={13} /> Enviar
          </PrimaryButton>
          <PrimaryButton type="button" variant="outline" testId="ia-clear" onClick={handleClear}>
            <RotateCcw size={13} /> Limpiar
          </PrimaryButton>
        </form>
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--cc-gray-500)", textAlign: "center", lineHeight: 1.4 }} data-testid="ia-disclaimer">
          Esta IA es una demostración simulada. No reemplaza el juicio humano.
        </div>
      </div>
    </Card>
  );
}

function Bubble({ autor, texto, agentName, ts }: { autor: "user" | "ia"; texto: string; agentName: string; ts?: string }) {
  const isUser = autor === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 12 }} data-testid={`ia-msg-${autor}`}>
      <div style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", gap: 8, maxWidth: "82%", alignItems: "flex-end" }}>
        <div
          style={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
            background: isUser ? "var(--cc-gray-300, #cbd5e1)" : "var(--cc-teal)",
            color: "white", display: "flex", alignItems: "center", justifyContent: "center",
          }}
          title={isUser ? "Tú" : agentName}
        >
          {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
        </div>
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            background: isUser ? "var(--cc-navy)" : "white",
            color: isUser ? "white" : "var(--cc-gray-800)",
            border: isUser ? "none" : "1px solid var(--cc-gray-200)",
            fontSize: 13, lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxShadow: isUser ? "none" : "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          {renderRich(texto)}
          {ts && (
            <div style={{ marginTop: 4, fontSize: 10, color: isUser ? "rgba(255,255,255,0.55)" : "var(--cc-gray-400)" }}>
              {new Date(ts).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }} data-testid="ia-typing">
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--cc-teal)", color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Bot size={14} />
        </div>
        <div style={{ padding: "10px 14px", borderRadius: 12, background: "white", border: "1px solid var(--cc-gray-200)", display: "flex", gap: 4 }}>
          <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
        </div>
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      style={{
        width: 6, height: 6, borderRadius: "50%", background: "var(--cc-teal)",
        animation: `ia-pulse 1s ${delay}ms infinite ease-in-out`,
        display: "inline-block",
      }}
    />
  );
}

function renderRich(text: string) {
  // Minimal **bold** + *italic* + line breaks
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });
}
